import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import AlertTemplate, { AlertTypes } from '@components/Alert/Alert';
import SupportTemplate from '@components/CartSupport/Support';
import { GlobalTooltipTemplate } from '@components/GlobalTooltip/GlobalTooltipTemplate';
import { Icons } from '@components/Icon';
import { ModalTypes } from '@components/Modal/Modal';
import TrainingBenefitTemplate from '@components/TrainingBenefit/TrainingBenefit';
import TrainingDatesTemplate from '@components/TrainingDates/TrainingDates';
import TrainingDescriptionTemplate from '@components/TrainingDescription/TrainingDescription';
import TrainingLecturerTemplate from '@components/TrainingLecturer/TrainingLecturer';
import TrainingProgramTemplate from '@components/TrainingProgram/TrainingProgram';
import TrainingStreamTemplate from '@components/TrainingStream/TrainingStream';
import useTranslations from '@hooks/useTranslation';
import TitleTemplate, { TitleTagTypes } from '@ui/SectionTitle/Title';
import { formatLecturerName } from '@utils/person.utils';
import { publicAssets } from '@utils/url.utils';
import moment from 'moment/moment';
import { ROUTES } from '../../constants';
import { ITrainingItem, TrainingViewTypes } from '../../interfaces/Calendar';
import { calendarApi } from '../../services/calendar.service';
import { RootState } from '../../store';
import { openModalType } from '../../store/ui';
import { breakpoints } from '../../styles/theme';
import ContentTemplate from '../../ui/Content/Content';
import { Banner, Content, DesktopTop, Favourite, FavouriteIcon, Header, Mobile, Sidebar } from './Training.elements';
import TrainingPackageTemplate from "@components/TrainingPackage/TrainingPackage";

export interface TrainingProps {
    training: ITrainingItem;
}

const TrainingTemplate: FC<TrainingProps> = ({ training }) => {
    const { t } = useTranslations();

    const router = useRouter();

    const dispatch = useDispatch();

    const [trainingVersion, setTrainingVersion] = useState(TrainingViewTypes.DESKTOP);
    const [hasError, setHasError] = useState(false);
    const [isFavorite, setIsFavorite] = useState(training?.isFavorite);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    const isLoggedInState = useSelector((state: RootState) => state.ui.isLoggedIn);

    useEffect(() => {
        if (window.innerWidth >= breakpoints.md) {
            setTrainingVersion(TrainingViewTypes.DESKTOP);
        }
        if (window.innerWidth < breakpoints.md) {
            setTrainingVersion(TrainingViewTypes.MOBILE);
        }
    });

    useEffect(() => {
        setIsLoggedIn(isLoggedInState);
    }, [isLoggedInState]);

    const formattedLecturers =
        training?.lecturers?.length > 0
            ? training?.lecturers?.map((lecturer) => ({
                  photoKey: lecturer.person.photo,
                  name: formatLecturerName(lecturer.person),
                  slug: lecturer.person.slug,
              }))
            : [];

    const handleOnFavoriteClick = async () => {
        try {
            setHasError(false);

            if (!isLoggedIn) {
                return dispatch(
                    openModalType({
                        type: ModalTypes.LOGIN,
                    })
                );
            }

            if (isFavorite) {
                await calendarApi.deleteCourseFromFavourite(training?.course?._key);
            } else {
                await calendarApi.addCourseToFavourite(training?.course?._key);
            }

            setIsFavorite(!isFavorite);
        } catch {
            setHasError(true);
        }
    };

    return (
        <>
            {hasError && <AlertTemplate type={AlertTypes.error} message={t('course.popular.error')} />}
            {trainingVersion === TrainingViewTypes.DESKTOP && (
                <ContentTemplate withPadding>
                    <DesktopTop>
                        <Content>
                            <Header>
                                <TitleTemplate tagType={TitleTagTypes.H1} withoutPadding alignLeft>
                                    {training?.course?.title}
                                </TitleTemplate>
                                <Favourite
                                    onClick={handleOnFavoriteClick}
                                    data-tooltip-id="favourite-tooltip"
                                    data-tooltip-content={
                                        isFavorite ? t('course.popular.remove') : t('course.popular.add')
                                    }
                                >
                                    <FavouriteIcon name={isFavorite ? Icons.FavouriteFull : Icons.Favourite} />{' '}
                                </Favourite>
                                <GlobalTooltipTemplate />
                            </Header>
                            <TrainingDescriptionTemplate
                                lecturers={formattedLecturers}
                                description={training?.course?.annotation}
                                courseKey={training?.course?._key}
                            />
                            <TrainingDatesTemplate
                                dates={training?.dates?.filter(
                                    (date) => moment().local().diff(date?.date?.to, 'minutes') <= 0
                                )}
                                isCustom={!!training?.course?.custom}
                                modules={training?.modules}
                            />
                            <TrainingStreamTemplate dates={training?.dates} />
                            <TrainingProgramTemplate
                                program={training?.course?.program}
                                detail={training?.course?.detail}
                                experience={training?.course?.experience_description}
                                experienceGallery={training?.course?.static_gallery}
                            />
                            {training?.lecturers?.map((lecturer, key) => (
                                <TrainingLecturerTemplate
                                    key={key}
                                    lecturer={lecturer.person}
                                    openedCourseId={training.course._id}
                                />
                            ))}
                            <SupportTemplate withoutMargin />
                        </Content>
                        <Sidebar>
                            <TrainingPackageTemplate modules={training?.modules} />
                            {(training?.course?.benefits?.length > 0 ||
                                training?.course?.bonuses?.length > 0 ||
                                training?.course?.schedule?.length > 0 ||
                                training?.course?.instructions?.length > 0 ||
                                training?.attachments?.length > 0) && (
                                <TrainingBenefitTemplate
                                    benefits={training?.course?.benefits}
                                    bonuses={training?.course?.bonuses}
                                    harmonogram={training?.course?.schedule}
                                    instructions={training?.course?.instructions}
                                    instructionAttachments={training?.attachments}
                                />
                            )}
                        </Sidebar>
                    </DesktopTop>
                </ContentTemplate>
            )}
            {trainingVersion === TrainingViewTypes.MOBILE && (
                <Mobile>
                    <Header>
                        <TitleTemplate tagType={TitleTagTypes.H1} withoutPadding alignLeft>
                            {training?.course?.title}
                        </TitleTemplate>
                        <Favourite
                            onClick={handleOnFavoriteClick}
                            data-tooltip-id="favourite-tooltip"
                            data-tooltip-content={isFavorite ? t('course.popular.remove') : t('course.popular.add')}
                        >
                            <FavouriteIcon name={isFavorite ? Icons.FavouriteFull : Icons.Favourite} />{' '}
                        </Favourite>
                        <GlobalTooltipTemplate />
                    </Header>
                    <TrainingDescriptionTemplate
                        lecturers={formattedLecturers}
                        description={training?.course?.annotation}
                        courseKey={training?.course?._key}
                        isFavorite={training?.isFavorite}
                    />
                    <TrainingPackageTemplate modules={training?.modules} />
                    <TrainingDatesTemplate
                        dates={training?.dates?.filter((date) => moment().local().diff(date?.date?.to, 'minutes') <= 0)}
                        isCustom={!!training?.course?.custom}
                        modules={training?.modules}
                    />
                    <TrainingStreamTemplate dates={training?.dates} />
                    <TrainingProgramTemplate
                        program={training?.course?.program}
                        detail={training?.course?.detail}
                        experience={training?.course?.experience_description}
                        experienceGallery={training?.course?.static_gallery}
                    />
                    <TrainingBenefitTemplate
                        benefits={training?.course?.benefits}
                        bonuses={training?.course?.bonuses}
                        harmonogram={training?.course?.schedule}
                        instructions={training?.course?.instructions}
                        instructionAttachments={training?.attachments}
                    />
                    {training?.lecturers?.map((lecturer, key) => (
                        <TrainingLecturerTemplate
                            key={key}
                            lecturer={lecturer.person}
                            openedCourseId={training.course._id}
                        />
                    ))}
                    <SupportTemplate />
                </Mobile>
            )}
        </>
    );
};

export default TrainingTemplate;
